import { useSelector } from 'react-redux'

import { selectLanguage } from 'root-redux/selects/common'
import {
  selectUserConfigData,
  selectUserCountryCode,
  selectUserGeoState,
  selectUserStatus,
} from 'root-redux/selects/user'

import { isUSUser } from 'helpers/isUSUser'

import {
  COOKIE_CONSENT_COHORTS,
  COOKIE_CONSENT_LANGUAGES,
  CookieConsentOption,
  EU_COUNTRY_CODES,
  US_STATES_WITH_COOKIE_CONSENT,
} from 'root-constants'

import { useCohortInfo } from './useCohortInfo'

export const useCookieConsentAnswer = () => {
  const userCountryCode = useSelector(selectUserCountryCode)
  const userStateCode = useSelector(selectUserGeoState)
  const userStatus = useSelector(selectUserStatus)
  const language = useSelector(selectLanguage)
  const config = useSelector(selectUserConfigData)
  const { cohortToUse } = useCohortInfo()

  const isEUUser = EU_COUNTRY_CODES.includes(userCountryCode.toUpperCase())
  const isUSUserCountry = isUSUser(userCountryCode)
  const cookieConsent = (config?.cookieConsent as string[]) || []
  const normalizedUserGeoState = userStateCode.toUpperCase()
  const isUSCookieConsentState = US_STATES_WITH_COOKIE_CONSENT.includes(
    normalizedUserGeoState,
  )
  const isCookieConsentLanguage = COOKIE_CONSENT_LANGUAGES.includes(language)
  const isCookieConsentCohort = COOKIE_CONSENT_COHORTS.includes(cohortToUse)

  const shouldApplyEUCookieConsent =
    isEUUser && isCookieConsentLanguage && isCookieConsentCohort

  const shouldApplyUSCookieConsent =
    isUSUserCountry &&
    isUSCookieConsentState &&
    isCookieConsentLanguage &&
    isCookieConsentCohort

  const shouldApplyCookieConsent =
    shouldApplyEUCookieConsent || shouldApplyUSCookieConsent

  const hasAnsweredConsent = cookieConsent.length > 0
  const hasAdvertisingConsent = cookieConsent.includes(
    CookieConsentOption.ADVERTISING_AND_TARGETING,
  )
  const hasFunctionalConsent = cookieConsent.includes(
    CookieConsentOption.FUNCTIONAL_COOKIES,
  )
  const hasAnalyticsConsent = cookieConsent.includes(
    CookieConsentOption.ANALYTICS_AND_PERFORMANCE,
  )

  const shouldShowCookieConsentBanner =
    shouldApplyCookieConsent && !hasAnsweredConsent

  const isCookieConsentEnabledForFlow =
    isCookieConsentLanguage && isCookieConsentCohort
  const isWaitingForCountryCode =
    isCookieConsentEnabledForFlow && !userCountryCode
  const isWaitingForUSUserStatus =
    isUSUserCountry && isCookieConsentEnabledForFlow && !userStatus
  const isWaitingForCookieConsentData =
    isWaitingForCountryCode || isWaitingForUSUserStatus

  const getConsentPermission = (hasCategoryConsent: boolean) => {
    if (isWaitingForCookieConsentData) {
      return false
    }

    if (!shouldApplyCookieConsent) {
      return true
    }

    return hasAnsweredConsent && hasCategoryConsent
  }

  const canUseAdvertising = getConsentPermission(hasAdvertisingConsent)
  const canUseFunctional = getConsentPermission(hasFunctionalConsent)
  const canUseAnalytics = getConsentPermission(hasAnalyticsConsent)

  return {
    isEUUser,
    isUSUser: isUSUserCountry,
    cookieConsent,
    hasAnsweredConsent,
    shouldApplyEUCookieConsent,
    shouldApplyUSCookieConsent,
    shouldApplyCookieConsent,
    shouldShowCookieConsentBanner,
    hasAdvertisingConsent,
    hasFunctionalConsent,
    hasAnalyticsConsent,
    canUseAdvertising,
    canUseFunctional,
    canUseAnalytics,
    isPersonalDataAllowed: canUseAdvertising,
  }
}
